import React, { FC, memo, useContext, useEffect, useMemo, useRef, useState } from "react"
import { List, Pagination, Space, Table } from "antd"
import { useTranslation } from "react-i18next"
import {
    ArrayDataOptions,
    CourseListFilters,
    CourseSearchDisplayMode,
    FilterInput,
    GetCoursesPublicQuery,
    useGetCourseListWithPaginationPublicQuery,
    GetCourseListWithPaginationPublicQuery,
    TableColumnsInput,
} from "../../generated/graphql"
import css from "./CourseTable.module.css"
import { rowKey, useCourseTableColumns } from "./CourseTableColumns"
import { renderShortInfoSubRow } from "../../views/TableColumnRenderers/TableColumnRenderers"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { SpecialFilters } from "./CourseTableContainer"
import { colorLightGrey } from "../../styles/colors"
import { AppSettings, SettingsContext } from "../../layouts/Main/MainLayout"
import CourseTableLegend from "./CourseTableLegend"
import { shouldShowMemberDiscount, shouldShowRestrictions } from "./courseTableHelpers"
import CourseCard from "./CourseCard"
import CourseGrid from "./CourseGrid"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import { useCourseFilterContext } from "./CourseFilterContext"
import { loadLastViewedCourse, clearLastViewedCourse } from "../../helpers/lastViewedCourse"
import { parentScrollCourseIntoView } from "../../helpers/parentIFrame"
import { InterestListItem } from "../interest/interestListData"
import InterestListSections, { InterestCard } from "../interest/InterestListSections"
import { sortInterestListsByAge } from "../interest/interestGroups"
import { buildInterestRows, isInterestRow, renderInterestCell } from "../interest/interestRowHelpers"
import { pickReportableFilters, reportEndUserEvent } from "../../services/reportEndUserEvent"

export type TableType = GetCoursesPublicQuery["coursesPublic"][0]

type CourseTableProps = {
    forceReload?: boolean
    filterInput?: Array<FilterInput>
    specialFilters?: SpecialFilters
    courseListFilters?: CourseListFilters
    selectedId: Function
    appSettings: AppSettings
    style?: React.CSSProperties
    // Interessenten-Listen folgen hinter dem letzten Kurs in derselben Pagination.
    // showCourseRows=false blendet die Kurse aus (listType=interest).
    interestLists?: Array<InterestListItem>
    showCourseRows?: boolean
}

export type TableDataType = {
    items: GetCourseListWithPaginationPublicQuery["coursesWithPaginationPublic"]["items"] | undefined
    loading: boolean
    error: any
    total: GetCourseListWithPaginationPublicQuery["coursesWithPaginationPublic"]["total"] | undefined
}

const CourseTable: FC<CourseTableProps> = ({
    forceReload,
    filterInput,
    courseListFilters,
    specialFilters,
    selectedId,
    appSettings,
    style,
    interestLists = [],
    showCourseRows = true,
}) => {
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const { t } = useTranslation()

    // Settings mit defensiven Fallbacks: Falls das Backend für ein neues Feld
    // unerwartet null/undefined liefert (z.B. alte Backend-Version), greifen die
    // Defaults — gleiche Werte wie die DB-Migration und der Admin-Default.
    const settings = useContext(SettingsContext)
    const displayMode = settings.courseSearchDisplayMode ?? CourseSearchDisplayMode.Table
    const pageSize = settings.courseSearchPageSize ?? 10
    const maxColumns = settings.courseSearchGridMaxColumns ?? 3
    const gridGap = settings.courseSearchGridGap ?? 15

    const [options, setOptions] = useState<ArrayDataOptions>(() => ({ limit: pageSize, offset: 0 }))
    const [showMemberDiscount, setShowMemberDiscount] = useState(false)

    const pollIntervalInMinutes = 10
    const pollInterval = pollIntervalInMinutes * 60 * 1000

    // Wenn der Admin pageSize zur Laufzeit ändert, holen wir das ein und springen
    // bewusst zurück auf Seite 1 — das vermeidet "off-by-N"-Zustände.
    useEffect(() => {
        setOptions((prev) => {
            if (prev.limit === pageSize) return prev
            return { ...prev, limit: pageSize, offset: 0 }
        })
    }, [pageSize])

    // Variablen memoisieren: Ohne useMemo erzeugt jeder Render eine neue Objekt-Identität
    // für `filters`, was zusammen mit fetchPolicy "cache-and-network" bei JEDEM Re-Render
    // einen Netzwerk-Request auslöst (Flacker-/Refetch-Loop).
    //
    // Bis 08/2026 wurde hier `locationId` auf null gezwungen, sobald eine Stadt gesetzt
    // war. Das war ein Workaround gegen eine überstrenge Backend-Bedingung, der den
    // Ortsfilter wirkungslos machte: Wer ein bestimmtes Bad wählte, bekam alle Bäder
    // der Stadt. Seit ko-X-main#612 werten Stadt und Ort gemeinsam aus (UND), der
    // Workaround entfällt ersatzlos.
    const variables = useMemo(() => {
        return { options, filters: courseListFilters }
    }, [options, courseListFilters])

    const {
        data: courseData,
        loading: loadingCourses,
        error: errorCourses,
        refetch: refetchCourses,
    } = useGetCourseListWithPaginationPublicQuery({
        variables,
        fetchPolicy: "cache-and-network",
        pollInterval,
    })

    const tableData: TableDataType = {
        items: courseData?.coursesWithPaginationPublic?.items,
        loading: loadingCourses,
        error: errorCourses,
        total: courseData?.coursesWithPaginationPublic?.total,
    }

    // Kundenakte: Eine leere Liste ist serverseitig unsichtbar (die Query läuft
    // anonym). Eingeloggte Kunden melden sie deshalb fire-and-forget; der Server
    // zählt dazu, wie viele Kurse eine Buchungsbeschränkung ausgeblendet hat.
    // Dedupe je Filterkombination und Session liegt im Service.
    useEffect(() => {
        if (!showCourseRows || loadingCourses || errorCourses || tableData.total !== 0) return
        reportEndUserEvent({ type: "CourseListEmpty", step: "CourseList", filters: pickReportableFilters(courseListFilters) })
    }, [showCourseRows, loadingCourses, errorCourses, tableData.total, courseListFilters])

    // Gemeinsame Pagination: Kurse zuerst (serverseitig paginiert), dahinter die
    // Interessenten-Listen (liegen komplett im Browser). Die Seitenzahlen zählen
    // beides; der Browser füllt den Rest jeder Seite mit dem passenden Ausschnitt
    // der Listen auf. Hinter dem letzten Kurs liefert der Server leere Seiten,
    // die nur noch Listen zeigen. Listen stehen nach Alter der Kursart sortiert.
    const offset = options.offset ?? 0
    const courseItems = showCourseRows ? tableData.items ?? [] : []
    const restrictiveCoursesShown = shouldShowRestrictions(courseListFilters?.restrictiveCourses, courseItems)
    const sortedInterestLists = sortInterestListsByAge(interestLists)
    // undefined = Kurs-Gesamtzahl noch nicht geladen → noch keine Listen anhängen,
    // sonst flackern Listen von Seite 1 kurz auf und verschwinden wieder.
    const courseTotal = showCourseRows ? tableData.total : 0
    const interestStart = Math.max(0, offset - (courseTotal ?? 0))
    const pageInterestLists =
        courseTotal === undefined ? [] : sortedInterestLists.slice(interestStart, interestStart + Math.max(0, pageSize - courseItems.length))
    const paginationTotal = courseTotal === undefined ? undefined : courseTotal + sortedInterestLists.length
    const interestRows = buildInterestRows(pageInterestLists)
    const hasInterestRows = interestRows.length > 0
    // Mobile rendert Kurse und Interessenten-Listen in getrennten Collections.
    // Sind auf der Seite nur Interessenten-Listen, darf die leere Kurs-Collection
    // keinen Leertext anzeigen.
    const showCourseCollection = showCourseRows && (loadingCourses || courseItems.length > 0 || !hasInterestRows)
    const coursesLoaded = !showCourseRows || Array.isArray(tableData.items)

    // Leere Seite (weder Kurse noch Interessenten-Listen): zurück auf Seite 1
    useEffect(() => {
        if (coursesLoaded && courseItems.length === 0 && pageInterestLists.length === 0 && offset > 0) {
            setOptions({ ...options, offset: 0 })
        }
    }, [options, offset, coursesLoaded, courseItems.length, pageInterestLists.length])

    // CHECK IF MEMBER DISCOUNT SHOULD BE SHOWN
    React.useEffect(() => {
        const showMemberDiscount = shouldShowMemberDiscount(courseData?.coursesWithPaginationPublic?.items)
        setShowMemberDiscount(showMemberDiscount)
    }, [courseData])

    // SCROLL ZURÜCK ZUM ZULETZT ANGESEHENEN KURS ----------------------------------------------
    // Beim Zurück aus den Kursdetails: zum zuletzt angesehenen Kurs scrollen (Eltern-Viewport
    // via parentIFrame, No-Op ohne enablePublicMethods) und ihn kurz hervorheben. Der Guard
    // verhindert wiederholtes Feuern bei Polling/Refetch (fetchPolicy "cache-and-network").
    const ctx = useCourseFilterContext()
    const lastViewedCourseKey = ctx?.lastViewedCourseKey
    const didScrollToLastViewed = useRef(false)
    useEffect(() => {
        if (didScrollToLastViewed.current) return
        if (!lastViewedCourseKey) return
        const items = tableData.items
        // Warten, bis die Liste geladen ist.
        if (!items || items.length === 0) return

        const lastId = loadLastViewedCourse(lastViewedCourseKey)
        if (!lastId) return

        // Nur scrollen, wenn der Kurs auf der aktuell sichtbaren Seite ist (sonst No-Op,
        // z.B. nach Wechsel auf eine andere Pagination-Seite). Der gemerkte Wert bleibt
        // dann erhalten, bis er tatsächlich verwendet werden kann.
        if (!items.some((item) => item.id === lastId)) return

        // Ab hier wird der gemerkte Kurs konsumiert -> Guard setzen und Key löschen,
        // damit der Effekt nicht erneut feuert.
        didScrollToLastViewed.current = true
        clearLastViewedCourse(lastViewedCourseKey)

        const anchorId = `course-${lastId}`
        // requestAnimationFrame: dem DOM einen Frame Zeit geben, bis das Anker-Element
        // (Card-div bzw. Table-<tr>) tatsächlich gerendert ist.
        requestAnimationFrame(() => {
            const element = document.getElementById(anchorId)
            if (!element) return
            // Geometrie des Kurses relativ zum iFrame-Dokumentanfang messen und den
            // zentralen Eltern-Wrapper das Minimal-Scrolling berechnen lassen (nur
            // dort ist die Höhe eines fixed Kundenheaders bekannt). Ziel: Kurs sichtbar;
            // iFrame-Oberkante sichtbar lassen wenn möglich, sonst Kurs mittig.
            const rect = element.getBoundingClientRect()
            parentScrollCourseIntoView(rect.top + window.scrollY, rect.height)
            // Table-<tr> braucht eine andere Highlight-Variante (box-shadow clippt dort).
            const isTableRow = element.tagName === "TR"
            const highlightClass = isTableRow ? css.courseHighlightRow : css.courseHighlight
            element.classList.add(highlightClass)
            window.setTimeout(() => {
                element.classList.remove(highlightClass)
            }, 2000)
        })
        // tableData.items als Dep, damit der Effekt nach dem Laden erneut prüft.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData.items, lastViewedCourseKey])

    const [columnsCount, setColumnsCount] = useState(0)
    const columns = useCourseTableColumns({ columnsCount: columnsCount })

    // CUSTOMER COLUMNS ----------------------------------------------------------------------------------

    // Null-Guard: settingEndUserAppPublic kann courseTable = null liefern
    // (Bestandszeilen ohne Spalten-Konfiguration); der Settings-Merge in
    // MainLayout überschreibt den {}-Default dann mit null → ohne Guard
    // crashte hier die komplette Kursliste.
    const courseTable = useContext(SettingsContext).courseTable
    const customerColumns = courseTable?.columns

    const columnStatusIsEnabled = Boolean(customerColumns?.find((column: TableColumnsInput) => column.name === "status" && column.enabled === true))

    // create an array of all enabled customerColumn names
    const enabledColumns = customerColumns?.filter((column: TableColumnsInput) => column.enabled === true).map((column: TableColumnsInput) => column.name)

    if (restrictiveCoursesShown) {
        enabledColumns?.push("restrictions")
    }

    useEffect(() => {
        setColumnsCount(enabledColumns?.length ?? 0)
    }, [enabledColumns])

    const columnsToUse = columns.filter((column) => enabledColumns?.includes(column.key))
    const columnsToUseWithTitles = columnsToUse.map((column) => {
        const name = column.key
        const customerColumn = customerColumns?.find((column: TableColumnsInput) => column.name === name)
        if (customerColumn) {
            return { ...column, title: customerColumn.label }
        }
        return column
    })

    // --- Konzept "Kurzinfo als volle Unterzeile" (nur Desktop-Table) ---------------------
    // Die Kurzinfo wird im Desktop-Table nicht mehr als eigene (schmale) Spalte gerendert
    // (langer Kundentext blähte dort die gesamte Zeilenhöhe auf), sondern als dauerhaft
    // aufgeklappte Unterzeile über die volle Tabellenbreite. Mobile (Card-List) und Grid
    // bleiben unverändert, da sie weiterhin columnsToUse nutzen.
    const kurzinfoColumn = columnsToUseWithTitles.find((column) => column.key === "additionalShortInfo")
    const kurzinfoEnabled = Boolean(kurzinfoColumn)
    const kurzinfoLabel = (kurzinfoColumn?.title as string) || "Kurzinfo"
    const tableColumns = kurzinfoEnabled ? columnsToUseWithTitles.filter((column) => column.key !== "additionalShortInfo") : columnsToUseWithTitles
    const kurzinfoExpandedRowKeys = useMemo(
        () => (kurzinfoEnabled ? (tableData.items ?? []).filter((item) => item.additionalShortInfo).map((item) => item.id) : []),
        [tableData.items, kurzinfoEnabled]
    )

    // Kurs-Spalten interest-fähig machen: an EINER Stelle wird jeder Column-Render
    // auf Interest-Zeilen (Marker) umgeleitet, statt die Kurs-Renderer in eine
    // diskriminierte Union zu zwingen.
    const interestAwareColumns = tableColumns.map((column) => ({
        ...column,
        render: (text: any, record: any, index: number) =>
            isInterestRow(record) ? renderInterestCell(column.key, record.__interestList, t) : column.render ? column.render(text, record, index) : text,
    }))
    // Kurse + Interest-Zeilen für den Desktop-Table. rowKey nutzt record.id (beide haben id).
    const desktopDataSource = [...courseItems, ...interestRows] as typeof courseItems

    const currentPage = useMemo(() => {
        const offset = options?.offset ?? 0
        return Math.floor(offset / pageSize) + 1
    }, [options?.offset, pageSize])

    const handlePaginationChange = (page: number, nextPageSize: number) => {
        setOptions({
            limit: nextPageSize,
            offset: (page - 1) * nextPageSize,
        })
    }

    // Eigenständige Pagination unter Kursen und Interessenten-Listen (Table und
    // Mobile); das Grid bringt seine eigene mit.
    const pager = (paginationTotal ?? 0) > pageSize && (
        <div className={css.courseTablePagination}>
            <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={paginationTotal}
                showSizeChanger={false}
                // Gesamtanzahl links neben den Seitenzahlen statt in eigener Zeile.
                showTotal={(total) => `Gesamtanzahl ${total}`}
                simple={media === "xs" || media === "sm"}
                responsive
                onChange={handlePaginationChange}
            />
        </div>
    )

    // LEERE LISTE — HINWEIS AUF VERSTECKTE KURSE (Issue #769) -------------------------------
    // Die Public-Query liefert zur leeren Liste, wie viele Kurse eine aktive
    // Buchungsbeschränkung ausgeblendet hat. Der Hinweis ist der einzige Weg für
    // anonyme Besucher, überhaupt von diesen Kursen zu erfahren. Felder sind
    // optional: ein älteres Backend liefert sie nicht, dann bleibt es beim Leertext.
    const hiddenCoursesCount =
        (courseData?.coursesWithPaginationPublic?.hiddenPrerequisiteCount ?? 0) + (courseData?.coursesWithPaginationPublic?.hiddenMembersOnlyCount ?? 0)

    const emptyText = (
        <Space direction='vertical' size={4} style={{ minHeight: 100, textAlign: "center" }}>
            <h3 style={{ color: colorLightGrey, marginBottom: 0 }}>
                {t("courseFilters.empty.title", "Für diese Auswahl stehen derzeit keine Kurse zur Verfügung.")}
            </h3>
            {hiddenCoursesCount > 0 && (
                <span style={{ color: colorLightGrey }} data-testid='course-table-hidden-hint'>
                    {t(hiddenCoursesCount === 1 ? "courseFilters.empty.hiddenCoursesOne" : "courseFilters.empty.hiddenCoursesOther", {
                        count: hiddenCoursesCount,
                        defaultValue:
                            hiddenCoursesCount === 1
                                ? "Es gibt einen weiteren Kurs, der nur nach Login oder über einen Freigabe-Link sichtbar ist. Bitte melden Sie sich an oder nutzen Sie den Link aus Ihrer E-Mail."
                                : "Es gibt {{count}} weitere Kurse, die nur nach Login oder über einen Freigabe-Link sichtbar sind. Bitte melden Sie sich an oder nutzen Sie den Link aus Ihrer E-Mail.",
                    })}
                </span>
            )}
        </Space>
    )

    // FEHLERZUSTAND ---------------------------------------------------------------------------
    // Ohne diese Anzeige drehte der Spinner bei Netzwerk-/GraphQL-Fehlern unbegrenzt weiter
    // (der Apollo-Error landete zwar in tableData.error, wurde aber nie gerendert).
    // Das Alert wird OBERHALB der jeweiligen Ansicht gerendert (statt sie zu ersetzen):
    // So bleiben bei Poll-/Refetch-Fehlern bereits geladene Kurse sichtbar.
    // Bewusst KEIN notifyOnNetworkStatusChange an der Query (loading=true bei jedem
    // 10-Minuten-Poll schickte die Tabelle regelmäßig in den Spinner-Overlay) —
    // das Retry-Feedback übernimmt der Button-Loading-Zustand in QueryErrorAlert.
    const errorAlert =
        errorCourses && !loadingCourses ? (
            <QueryErrorAlert testId='course-table-error' style={{ marginBottom: 15 }} message={t("coursesTable.loadErrorMessage")} onRetry={refetchCourses} />
        ) : null

    // LAYOUT MOBILE DEVICES (xs) — bleibt unverändert: Card-List ----------------------------
    // Wichtig: Mobile-Pfad ignoriert displayMode bewusst. Das Mobile-Verhalten (1-spaltige
    // Karten) ist seit jeher der schmale-Bildschirm-Fallback und soll unangetastet bleiben.

    if (media === "xs") {
        return (
            <div className={css.cardContainer} data-testid='course-table'>
                {errorAlert}
                {showCourseCollection && (
                    <List
                        style={{ width: "100%" }}
                        dataSource={courseItems}
                        renderItem={(course) => <CourseCard id={`course-${course.id}`} course={course} columnsToUse={columnsToUse} />}
                        loading={tableData.loading}
                        // Seitenzahlen stehen unter Kursen UND Interessenten-Listen (eine Pagination).
                        pagination={false}
                        locale={{ emptyText }}
                    />
                )}
                {hasInterestRows && <InterestListSections lists={pageInterestLists} columnsToUse={columnsToUse} />}
                {pager}
                <Space direction='vertical' size={2} style={{ display: "flex", alignItems: "center", paddingTop: 15 }}>
                    {(columnStatusIsEnabled || restrictiveCoursesShown) && (
                        <CourseTableLegend showStatus={columnStatusIsEnabled} showRestrictions={restrictiveCoursesShown} appSettings={appSettings} />
                    )}
                </Space>
            </div>
        )
    }

    // LAYOUT DESKTOP — GRID (NEU) -----------------------------------------------------------

    if (displayMode === CourseSearchDisplayMode.Grid) {
        return (
            <>
                {errorAlert}
                {(showCourseRows || hasInterestRows) && (
                    <CourseGrid
                        items={courseItems}
                        extraCards={pageInterestLists.map((list) => (
                            <InterestCard key={list.id} list={list} columnsToUse={columnsToUse} className={css.courseGridCard} />
                        ))}
                        loading={showCourseRows && tableData.loading}
                        total={paginationTotal}
                        columnsToUse={columnsToUse}
                        pageSize={pageSize}
                        maxColumns={maxColumns}
                        gap={gridGap}
                        currentPage={currentPage}
                        onPaginationChange={handlePaginationChange}
                        showLegendRestrictions={restrictiveCoursesShown}
                        showLegendMemberDiscount={showMemberDiscount}
                        showLegendStatus={columnStatusIsEnabled}
                        appSettings={appSettings}
                        emptyText={emptyText}
                    />
                )}
            </>
        )
    }

    // LAYOUT DESKTOP — TABLE (Default, unverändertes Verhalten zu vorher) ---------------------

    return (
        <>
            {errorAlert}
            <Table
                data-testid='course-table'
                className={css.kurzinfoTable}
                columns={interestAwareColumns}
                dataSource={desktopDataSource}
                loading={tableData.loading}
                size={"small"}
                rowKey={rowKey}
                expandable={
                    kurzinfoEnabled
                        ? {
                              expandedRowKeys: kurzinfoExpandedRowKeys,
                              showExpandColumn: false,
                              rowExpandable: (record) => !isInterestRow(record) && Boolean(record.additionalShortInfo),
                              expandedRowRender: (record) => renderShortInfoSubRow(record.additionalShortInfo, kurzinfoLabel),
                          }
                        : undefined
                }
                rowClassName={(record) => (kurzinfoEnabled && record.additionalShortInfo ? css.kurzinfoMainRow : "")}
                // Anker-id auf die Daten-Zeile (<tr>) für die Scroll-zurück-UX. Die
                // Kurzinfo-Expanded-Row bleibt bewusst ohne id (eigene <tr>).
                onRow={(record) => ({ id: `course-${record.id}` })}
                // Die Daten sind bereits serverseitig paginiert. Zusatzzeilen für
                // Interessentenlisten können die lokale dataSource über pageSize
                // vergrößern; Ant Table würde sie dann fälschlich ein zweites Mal
                // paginieren und auf späteren Seiten sämtliche Zeilen wegschneiden.
                pagination={false}
                footer={
                    // Legende auch ohne Status-Spalte, sonst blieben die Beschränkungs-Icons unerklärt.
                    // Die Gesamtanzahl steht in der Pagination darunter.
                    columnStatusIsEnabled || restrictiveCoursesShown
                        ? () => (
                              <Space
                                  style={
                                      mediaSize.media === "xl" || mediaSize.media === "xxl"
                                          ? {
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "space-between",
                                            }
                                          : {
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                alignItems: "flex-end",
                                                flexDirection: "column-reverse",
                                                fontSize: "0.9rem",
                                                opacity: 0.8,
                                                lineHeight: 1,
                                            }
                                  }
                              >
                                  <CourseTableLegend
                                      showStatus={columnStatusIsEnabled}
                                      showRestrictions={restrictiveCoursesShown}
                                      showMemberDiscount={showMemberDiscount}
                                      appSettings={appSettings}
                                  />
                              </Space>
                          )
                        : undefined
                }
                locale={{ emptyText }}
            />
            {pager}
        </>
    )
}

export default memo(CourseTable)
